import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { ExternalLink, Landmark, Link2, Radar } from "lucide-react";
import { useApi, useAuth, useInvalidateOn } from "@ncp/api-client";
import { authorityText, formatDate, useLocale, useTranslation, type Locale } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton, SourceText, InfoHint, useSave } from "@ncp/ui";
import { CatalogSearch } from "../dealwatch/CatalogSearch";
import { FilingBadge, ScoreRing } from "../dealwatch/DealBadges";
import { DEAL_REVIEWERS, dealKeys, fmtAedShort, useCaseDealIntelligence, type CaseDealRow } from "../dealwatch/api";
import { useHeadStandsIn } from "@/lib/decisionControls";

/** Related Mergermarket activity on an economic-concentration file: attached deals, party-overlap suggestions, announced-vs-form checks. */
export function CaseDealIntelligencePanel({ caseId, compact = false }: { caseId: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const headStandsIn = useHeadStandsIn();
  const canLink = (user?.roles ?? []).some((r) => DEAL_REVIEWERS.includes(r)) || headStandsIn;
  const briefing = useCaseDealIntelligence(caseId);
  useInvalidateOn(["dealwatch.updated"], [[...dealKeys.byCase(caseId)]]);

  const link = useSave({
    mutationFn: (dealId: string) => api.post(`/api/deal-watch/${dealId}/link-case`, { caseId, note: null }),
    saved: t("backoffice:analysisSections.dealIntelligence.linked"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: dealKeys.byCase(caseId) });
      void qc.invalidateQueries({ queryKey: ["case-review", caseId] });
    },
  });

  const openDeal = (id: string) => navigate(`/investigations?tab=deal-watch&deal=${id}`);
  const data = briefing.data;
  const linked = data?.linked ?? [];
  const suggested = data?.suggested ?? [];
  const news = data?.news ?? [];
  const checks = locale === "ar" ? (data?.checksAr ?? []) : (data?.checks ?? []);
  const total = linked.length + suggested.length;

  if (briefing.isLoading) return <Skeleton className={compact ? "h-24" : "h-48"} />;
  if (compact && total === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-base"><Radar className="size-4 text-aegold-700" /> {t("backoffice:analysisSections.dealIntelligence.title")} <Badge size="sm" variant="outline">{total}</Badge><InfoHint>{t("backoffice:analysisSections.dealIntelligence.hint")}</InfoHint></CardTitle>
          </div>
          {!compact ? <CatalogSearch value="" onCommit={() => undefined} onOpenDeal={openDeal} onOpenCompany={() => navigate("/investigations?tab=deal-watch")} /> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {total === 0 && news.length === 0 ? <p className="text-sm text-muted-foreground">{t("backoffice:analysisSections.dealIntelligence.empty")}</p> : null}
        {checks.length > 0 ? (
          <ul className="space-y-1.5 rounded-xl border border-warning-200 bg-warning-50 p-3 text-xs text-warning-950">
            {checks.map((c) => <li key={c}>{c}</li>)}
          </ul>
        ) : null}
        {linked.length > 0 ? (
          <DealGroup title={t("backoffice:analysisSections.dealIntelligence.onFile")} rows={linked} locale={locale} onOpen={openDeal} />
        ) : null}
        {suggested.length > 0 ? (
          <DealGroup
            title={t("backoffice:analysisSections.dealIntelligence.suggested")}
            rows={suggested}
            locale={locale}
            onOpen={openDeal}
            onLink={canLink ? (id) => link.mutate(id) : undefined}
            linking={link.isPending}
          />
        ) : null}
        {!compact && news.length > 0 ? (
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:analysisSections.dealIntelligence.news")}</p>
            <ul className="divide-y divide-sand-100">
              {news.map((n) => (
                <li key={n.externalId} className="py-2">
                  <p className="text-sm font-medium"><SourceText text={n.headline} /></p>
                  <p className="text-xs text-muted-foreground">
                    <Badge size="sm" variant="outline">{t(`backoffice:news.sources.${n.sourceKey ?? "news"}`, { defaultValue: "" }) || (n.sourceKey ? <bdi lang="en" dir="ltr">{n.sourceKey}</bdi> : t("backoffice:news.item"))}</Badge>
                    {n.when ? ` · ${formatDate(n.when, locale)}` : ""}
                    {n.companies.length ? <> · <SourceText text={n.companies.slice(0, 3).join(", ")} /></> : null}
                  </p>
                  {n.url ? <a href={n.url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-aegold-800 hover:underline"><ExternalLink className="size-3" /> {t("backoffice:news.openSource")}</a> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function DealGroup({ title, rows, locale, onOpen, onLink, linking }: { title: string; rows: CaseDealRow[]; locale: Locale; onOpen: (id: string) => void; onLink?: (id: string) => void; linking?: boolean }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-sand-600">{title}</p>
      <ul className="divide-y divide-sand-100 rounded-xl border border-sand-200">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-start gap-3 p-3">
            <ScoreRing score={row.score} size={40} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium"><SourceText text={row.title} /></p>
              <p className="text-xs text-muted-foreground">
                <SourceText text={row.parties.slice(0, 3).join(" · ")} />
                {row.valueAed != null ? ` · ${fmtAedShort(row.valueAed, locale)}` : ""}
                {row.targetStakePercent != null ? ` · ${row.targetStakePercent}%` : ""}
                {row.announcedOn ? ` · ${formatDate(row.announcedOn, locale)}` : ""}
              </p>
              {row.overlap.length ? <p className="mt-0.5 text-[11px] text-sand-700">{t("backoffice:analysisSections.dealIntelligence.overlap")}: <SourceText text={row.overlap.join(locale === "ar" ? "، " : ", ")} /></p> : null}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <FilingBadge status={row.filingStatus} />
                <Badge size="sm" variant="outline">{t(`backoffice:analysisSections.dealIntelligence.relation.${row.relation}`, { defaultValue: row.relation })}</Badge>
                {row.reviewEvents[0] ? <Badge size="sm" variant="outline">{authorityText(row.reviewEvents[0].agency, locale)}</Badge> : null}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {onLink ? <Button size="sm" variant="outline" className="h-7" loading={linking} onClick={() => onLink(row.id)}><Link2 /> {t("backoffice:analysisSections.dealIntelligence.link")}</Button> : null}
              <Button size="sm" variant="ghost" className="h-7" onClick={() => onOpen(row.id)}><Landmark /> {t("backoffice:analysisSections.dealIntelligence.open")}</Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
